import {
  useLoanApplication,
  useLoanRequest,
} from '@axo/shared/data-access/hooks';
import {
  ApplicationDataTabType,
  useApplicationDataStore,
} from '../../../store/applicationDataStore';
import { ReactNode, useMemo } from 'react';
import { UserName } from '../../UserName';
import { Icon, Tag } from '@blueprintjs/core';
import { formatDatetime } from '../../../utils/formatDate';
import { ApplicantMode } from '@axo/shared/data-access/types/loan_request';

interface ApplicationDataCardHeaderInfoProps {
  applicationID: string;
}

export const ApplicationDataCardHeaderInfo: React.FC<
  ApplicationDataCardHeaderInfoProps
> = ({ applicationID }) => {
  const { selectedTab } = useApplicationDataStore(applicationID);
  const application = useLoanApplication(applicationID);
  const loanRequest = useLoanRequest({
    loanApplicationID: applicationID,
    loanRequestID:
      selectedTab.type === ApplicationDataTabType.REQUEST
        ? selectedTab.requestID
        : undefined,
  });

  const createdBy = useMemo(() => {
    const channel = application.data?.Channels && application.data?.Channels[0];

    return channel?.InternalIdentityClientID ? (
      <UserName clientID={channel.InternalIdentityClientID} />
    ) : (
      'customer'
    );
  }, [application.data?.Channels]);

  if (selectedTab.type === ApplicationDataTabType.DRAFT)
    return <Tag minimal>Loan request draft</Tag>;

  const createdByTag = [
    ApplicationDataTabType.ORIGINAL,
    ApplicationDataTabType.AXO_VERSION,
  ].includes(selectedTab.type) ? (
    <Tag minimal>Original version: created by {createdBy}</Tag>
  ) : (
    <Tag minimal>
      Created by:{' '}
      {loanRequest.data &&
        typeof loanRequest.data.CreatedBy !== 'undefined' &&
        loanRequest.data.CreatedBy}
    </Tag>
  );

  const createdAtTag = selectedTab.type === ApplicationDataTabType.REQUEST && (
    <Tag minimal>
      Request created:{' '}
      {loanRequest.data &&
        typeof loanRequest.data.CreatedAt !== 'undefined' &&
        formatDatetime(loanRequest.data.CreatedAt)}
    </Tag>
  );

  let applicantionSentAsTag: ReactNode;
  if (loanRequest.data && loanRequest.data.CoApplicantPersonID) {
    const mode = loanRequest.data.ApplicantMode;
    const originalApplicantFirst =
      mode === ApplicantMode.ApplicantModeNormal ||
      mode === ApplicantMode.ApplicantModeMainApplicant;
    const sentWithAnCoApplicant =
      mode === ApplicantMode.ApplicantModeNormal ||
      mode === ApplicantMode.ApplicantModeSwap;

    const firstIcon = originalApplicantFirst ? 'person' : 'people';
    const secondIcon = !originalApplicantFirst ? 'person' : 'people';

    applicantionSentAsTag = (
      <Tag minimal>
        {'Applicant sent as '}
        <Icon icon={firstIcon} size={14} />
        {sentWithAnCoApplicant && (
          <>
            {' + '}
            <Icon icon={secondIcon} size={14} />
          </>
        )}
      </Tag>
    );
  }

  return (
    <>
      {createdAtTag}
      {createdByTag}
      {applicantionSentAsTag}
    </>
  );
};
